// ============================================================
//  ContestJoin.js — public join page  /c/:slug
//  Countdown, prize podium, join form → your own link (cmdkit.im/r/CODE), live score,
//  "X more to pass Y", leaderboard. Your number is remembered on this device.
// ============================================================
import './ContestPublic.css';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicContest, joinContest } from '../contact-saver/contestApi';

const KEY = slug => `ck_contest_${slug}`;
const dayName = s => (s ? new Date(`${s}T12:00:00`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }) : '');
const PLACE = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];
const initials = n => String(n || '?').split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();

/** Live countdown to the start (scheduled) or the end (live) — end of that day, local time. */
function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!target) return null;
  const ms = Math.max(0, target - now);
  return { d: Math.floor(ms / 86400000), h: Math.floor(ms / 3600000) % 24, m: Math.floor(ms / 60000) % 60, s: Math.floor(ms / 1000) % 60, done: ms === 0 };
}

export default function ContestJoin() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [f, setF] = useState({ name: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let saved = '';
    try { saved = localStorage.getItem(KEY(slug)) || ''; } catch { /* private mode */ }
    getPublicContest(slug, saved).then(d => {
      if (!d.success) { setErr(d.message || 'Contest not found'); return; }
      setData(d); document.title = d.contest.name;
    }).catch(() => setErr('Couldn’t load the contest. Check your connection.'));
  }, [slug]);

  const c = data?.contest;
  const target = useMemo(() => {
    if (!c) return null;
    if (c.state === 'scheduled') return new Date(`${c.startsOn}T00:00:00`).getTime();
    if (c.state === 'live') return new Date(`${c.endsOn}T23:59:59`).getTime();
    return null;
  }, [c]);
  const left = useCountdown(target);

  async function join(e) {
    e.preventDefault(); setBusy(true); setFormErr('');
    try {
      const d = await joinContest(slug, f.name, f.phone);
      if (!d.success) { setFormErr(d.message); setBusy(false); return; }
      try { localStorage.setItem(KEY(slug), f.phone); } catch { /* ignore */ }
      setData(x => ({ ...x, me: d.me, board: d.board, stats: d.stats || x.stats }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch { setFormErr('Couldn’t join. Try again.'); }
    setBusy(false);
  }
  function copy() {
    try { navigator.clipboard.writeText(data.me.link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  }
  function signOut() { try { localStorage.removeItem(KEY(slug)); } catch { /* ignore */ } setData(x => ({ ...x, me: null })); }

  if (err) return <main className="cpj cpj-center"><p>{err}</p></main>;
  if (!data) return <main className="cpj cpj-center"><span className="cpj-spin" aria-label="Loading" /></main>;
  const me = data.me;
  const closed = c.state === 'ended' || c.state === 'paused';
  const who = c.business || 'us';
  const prizes = c.prizes?.length ? c.prizes : (c.prize ? [c.prize] : []);
  const shareText = me ? `Join me in the ${c.name}! Tap to chat with ${who} on WhatsApp: ${me.link}` : '';
  const board = data.board || [];
  // who's just above me, and how many more friends to pass them
  const above = me && me.rank > 1 ? board.find(r => r.rank === me.rank - 1) : null;
  const gap = above ? above.count - me.count + 1 : 0;

  return (
    <main className="cpj">
      <header className="cpj-hero">
        <div className="cpj-top">
          <span className="cpj-biz"><i aria-hidden="true">{initials(who)}</i>{who}</span>
          <span className={`cpj-badge is-${c.state}`}>{c.state === 'live' ? 'Live now' : c.state === 'scheduled' ? 'Starting soon' : c.state === 'paused' ? 'Paused' : 'Ended'}</span>
        </div>
        <h1>{c.name}</h1>
        <p className="cpj-tag">Invite friends to chat with {who} on WhatsApp. The most new friends wins.</p>
        {left && !closed && (
          <div className="cpj-count" aria-label={`${c.state === 'scheduled' ? 'Starts' : 'Ends'} in ${left.d} days ${left.h} hours`}>
            <span className="cpj-count-k">{c.state === 'scheduled' ? 'Starts in' : 'Ends in'}</span>
            <div>
              <span><b>{left.d}</b><small>days</small></span>
              <span><b>{String(left.h).padStart(2, '0')}</b><small>hrs</small></span>
              <span><b>{String(left.m).padStart(2, '0')}</b><small>min</small></span>
              <span><b>{String(left.s).padStart(2, '0')}</b><small>sec</small></span>
            </div>
          </div>
        )}
        {closed && <p className="cpj-closed-tag">{c.state === 'ended' ? `Ended ${dayName(c.endsOn)} — thanks to everyone who took part.` : 'Paused for now — check back soon.'}</p>}
      </header>

      {prizes.length > 0 && (
        <section className="cpj-prizes" aria-labelledby="cpj-pz">
          <h2 id="cpj-pz" className="cpj-sec">Prizes</h2>
          <div className="cpj-pod">
            {prizes.slice(0, 3).map((p, i) => (
              <div key={i} className={`cpj-pz is-${i + 1}`}><span className="cpj-pz-place">{PLACE[i]}</span><b>{p}</b></div>
            ))}
          </div>
          {prizes.length > 3 && (
            <ul className="cpj-more">{prizes.slice(3).map((p, i) => <li key={i}><span>{PLACE[i + 3]}</span>{p}</li>)}</ul>
          )}
        </section>
      )}

      <div className="cpj-proof">
        <span><b>{data.stats?.participants ?? 0}</b> taking part</span>
        <span><b>{data.stats?.referrals ?? 0}</b> friends invited</span>
        <span>Ends <b>{dayName(c.endsOn)}</b></span>
      </div>

      <div className="cpj-body">
        {me ? (
          <>
            <section className="cpj-me" aria-labelledby="cpj-hi">
              <h2 id="cpj-hi">You’re in, {me.name.split(' ')[0]}</h2>
              <div className="cpj-stats">
                <div><b>{me.count}</b><span>friends</span></div>
                <div><b>#{me.rank}</b><span>your rank</span></div>
                <div><b className="cpj-up">+{me.today}</b><span>today</span></div>
              </div>
              {me.rank === 1 && me.count > 0 ? <p className="cpj-nudge is-top">You’re in the lead — keep sharing to stay there.</p>
                : above ? <p className="cpj-nudge"><b>{gap} more friend{gap === 1 ? '' : 's'}</b> to pass {above.name} and reach #{above.rank}.</p>
                : <p className="cpj-nudge">Share your link to get your first friend in.</p>}
            </section>

            <section className="cpj-link" aria-labelledby="cpj-ln">
              <span id="cpj-ln" className="cpj-k">Your invite link</span>
              <div className="cpj-row"><b className="cpj-url">{me.link.replace(/^https?:\/\//, '')}</b>
                <button type="button" className="cpj-copy" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button></div>
              <span className="cpj-note">Opens WhatsApp with {who} and a message carrying your code <b>{me.code}</b>. Friends just tap send.</span>
              <div className="cpj-2">
                <a className="cpj-wa" href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer">
                  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.2-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.1 5.1 0 0 0 1.1 2.7 11.6 11.6 0 0 0 4.4 3.9c1.6.7 2.3.8 3.1.6a2.7 2.7 0 0 0 1.8-1.2 2.2 2.2 0 0 0 .2-1.3c-.1-.1-.3-.2-.5-.3Z" /></svg>
                  Share on WhatsApp</a>
                {typeof navigator !== 'undefined' && navigator.share
                  ? <button type="button" className="cpj-ghost" onClick={() => navigator.share({ title: c.name, text: shareText }).catch(() => {})}>Other apps</button>
                  : <button type="button" className="cpj-ghost" onClick={copy}>Copy link</button>}
              </div>
            </section>
          </>
        ) : closed ? null : (
          <section className="cpj-join" aria-labelledby="cpj-jt">
            <h2 id="cpj-jt">Get your invite link</h2>
            <ol className="cpj-steps">
              <li><span>1</span><div><b>Join below</b><small>You get your own WhatsApp link</small></div></li>
              <li><span>2</span><div><b>Share it</b><small>On your status, in groups, with friends</small></div></li>
              <li><span>3</span><div><b>Climb the board</b><small>Every new friend who messages {who} counts</small></div></li>
            </ol>
            <form className="cpj-form" onSubmit={join}>
              <label>Your name<input value={f.name} maxLength={60} onChange={e => setF(x => ({ ...x, name: e.target.value }))} autoComplete="name" required placeholder="e.g. Tolu Adeyemi" /></label>
              <label>Your WhatsApp number<input inputMode="tel" value={f.phone} onChange={e => setF(x => ({ ...x, phone: e.target.value }))} placeholder="0803 123 4567" autoComplete="tel" required />
                <small>Only used to show your score. It isn’t shared.</small></label>
              {formErr && <p className="cpj-err" role="alert">{formErr}</p>}
              <button type="submit" className="cpj-go" disabled={busy}>{busy ? 'Getting your link…' : 'Get my invite link'}</button>
              <small className="cpj-center-t">Already joined? Enter the same number to see your score.</small>
            </form>
          </section>
        )}

        {board.length > 0 && (
          <section className="cpj-board" aria-labelledby="cpj-top">
            <div className="cpj-board-h"><h2 id="cpj-top">Leaderboard</h2><small>Top {board.length}</small></div>
            <ol>
              {board.map(r => {
                const mine = me && r.rank === me.rank;
                return (
                  <li key={r.rank} className={`${mine ? 'is-me' : ''} ${r.rank <= 3 ? `is-p${r.rank}` : ''}`}>
                    <span className="cpj-rk">{r.rank}</span>
                    <span className="cpj-av" aria-hidden="true">{initials(r.name)}</span>
                    <span className="cpj-nm">{r.name}{mine && <em>you</em>}</span>
                    <span className="cpj-ct"><b>{r.count}</b>{r.today > 0 && <small>+{r.today} today</small>}</span>
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        <section className="cpj-rules" aria-labelledby="cpj-rl">
          <h2 id="cpj-rl" className="cpj-sec">How it counts</h2>
          <ul>
            <li>Only <b>new</b> people who haven’t chatted with {who} before count.</li>
            <li>Each friend counts once — for the first code they send.</li>
            <li>Your own number doesn’t count. New friends show up within about 5 minutes.</li>
            <li>Runs {dayName(c.startsOn)} – {dayName(c.endsOn)}.</li>
          </ul>
        </section>
        {me && <button type="button" className="cpj-link-btn" onClick={signOut}>Not you? Use a different number</button>}
        <small className="cpj-fine">Powered by CommandKit</small>
      </div>
    </main>
  );
}